<script lang="ts" setup>
import {
  useAsyncData,
  useCreators,
  useMaps,
  useRouter,
  useSpots,
} from '#imports';
const { fetchRecentMaps, fetchDestinations } = useMaps();
const { fetchRecentCreators } = useCreators();

const router = useRouter();

const { data: maps, pending: mapsPending } = useAsyncData('explore-maps', () =>
  fetchRecentMaps(8),
);
const { data: destinations } = await useAsyncData('featuredDestinations', () =>
  fetchDestinations(8),
);
const { data: creators } = await useAsyncData('featuredCreators', () =>
  fetchRecentCreators(15),
);
</script>

<template>
  <div class="explore-page">
    <MPSectionHeader title="Expert Picks by Global Tastemakers" size="large" subtitle="Explore Mappr" summary="Curated lists from local experts and global creators." class="page-title"/>
    <section hydrate-on-visible>
      <h2>Featured Spots</h2>
      <MPViewportSpill show-controls class="spots">
        <LazyMPSpotCard
          class="spot-item"
          v-for="map in maps"
          :key="map.spots[0].id"
          :creator="map.profiles"
          actions
          :spot="map.spots[0]"
          show-destination
          showTier
        />
      </MPViewportSpill>
    </section>
    <section hydrate-on-visible>
      <h2>What's New</h2>
      <NuxtLinkLocale to="/maps" class="see-more">See more</NuxtLinkLocale>
      <MPViewportSpill show-controls class="maps">
        <LazyMPMapCard class="map-item" v-for="map in maps" :key="map.id" :map="map" hide-details/>
      </MPViewportSpill>
    </section>
    <!-- <section hydrate-on-visible>
      <h2>Recents</h2>
      Stuff the user looked at already
    </section>
    <section hydrate-on-visible>
      <h2>Fresh Finds</h2>
      single new map
    </section> -->
    <section hydrate-on-visible>
      <h2>Creators</h2>
      <MPViewportSpill show-controls class="creators">
        <NuxtLinkLocale :to="`/@${creator.mappr_handle}`" v-for="creator in creators" :key="creator.id" class="creator-item">
          <LazyMPImg :src="creator.profile_image_url" alt="Creator Avatar" :width="200" :height="200"/>
          @{{ creator.mappr_handle }}
        </NuxtLinkLocale>
      </MPViewportSpill>
    </section>
    <section hydrate-on-visible>
      <h2>Destinations</h2>
      <NuxtLinkLocale to="/destinations" class="see-more">See more</NuxtLinkLocale>
      <MPViewportSpill show-controls class="destinations">
        <div
          v-for="destination in destinations"
          :key="destination.id"
          class="destinations-item"
          :style="`background-image: url(${destination.cover_image_url})`"
        >
          <LazyNuxtLinkLocale :to="`/destinations/${destination.id}`">
            <div class="destinations-item-content">
              <h3>{{ destination.name }}</h3>
              <small>{{ destination.country }}</small>
            </div>
          </LazyNuxtLinkLocale>
        </div>
      </MPViewportSpill>
    </section>
    <MPUnlockCta class="cta" :show-counts="false"/>
  </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/layouts.pcss';
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/typography.pcss';
$gridGap:1rem;
.explore-page {
  @mixin layout-wrapper-wide;
  margin: 1rem auto 2rem;
  @mixin screen md {
    margin: 6rem auto 2rem;
  }
  section {
    position: relative;
  }
  h2 {
    @mixin typeface-h4;
    padding: 1rem;
    margin: 0.75rem 0 0;
  }

  .creator-item {
    min-width: 7rem;
  }

  .map-item {
    min-width: 15rem;
  }


  .spot-item {
    min-width: 20rem;
    height: 25rem;
  }

  .page-title {
    margin-bottom: 1rem;
    @mixin screen md {
      margin-bottom: 4rem;
    }
  }

  .see-more {
    position: absolute;
    top: 1.5rem;
    right: 1rem;
    color: var(--color-slate-500);
    @mixin screen md {
      right: 0;
    }
  }

  .maps {
    width: 100%;
    gap: $gridGap;
    &:deep(.map-card) {
      width: calc(($layoutWide / 5) - $gridGap);
    }
    &:deep(.map-card:nth-child(1)) {
      margin-left: 1rem;
    }
  }
  .spots {
    width: 100%;
    gap: $gridGap;
    &:deep(.spot) {
      max-width: calc(($layoutWide / 5) - $gridGap);
    }
    &:deep(.spot:nth-child(1)) {
      margin-left: 1rem;
    }
  }
  .creators {
    width: 100%;
    text-align: center;
    gap: $gridGap;
    .creator-item:nth-child(1) {
      margin-left: 1rem;
    }
    .creator-item img {
      width: calc(($layoutWide / 8) - $gridGap);
      border-radius: 100%;
      aspect-ratio: 1/1;
      object-fit: cover;
      margin-bottom: 0.5rem;
    }
  }
  .cta {
    margin: 1rem;
  }
  .destinations {
    display: grid;
    gap: $gridGap;
    .destinations-item:nth-child(1) {
      margin-left: 1rem;
    }
    .destinations-item {
      height: 12rem;
      width: calc(($layoutWide / 5) - $gridGap);
      border-radius: 1rem;
      background-position: center;
      background-size: cover;
      color: var(--color-slate-50);
      background-color: var(--color-slate-500);
      @mixin screen sm {
        height: 18rem;
      }
    }
    .destinations-item-content {
      text-align: left;
      border-radius: 1rem;
      display: flex;
      flex-direction: column;
      align-items: bottom;
      padding: 1rem;
      width: 100%;
      height: 100%;
      background: linear-gradient(-20deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.7) 100%);
      h3 {
        margin: 0;
        @mixin typeface-h5;
      }
      small {
        color: var(--color-slate-200);

      }
    }
  }
}
</style>
